import { Head } from "@inertiajs/react"

import { formatDate, formatUSDate } from "@/lib/date-utils"
import { formatPhone } from "@/lib/format-phone"
import { ensureUrl } from "@/lib/format-url"
import { objectPositionStyle } from "@/lib/utils"

/* ── Types ──────────────────────────────────────────────── */

interface CaseRecord {
  id: number
  case_number: string
  file_number: string | null
  display_name: string
  hpi_name: string | null
  customer_name: string | null
  investigation_type: string | null
  manager: string | null
  open_date: string | null
  source: string | null
  notes: string | null
  objectives: string | null
  has_attorney: boolean
  attorney_name: string | null
  attorney_phone: string | null
  attorney_law_firm: string | null
  attorney_email: string | null
  results_notes: string | null
  accepted_text: string | null
  open: boolean
}

interface CustomerData {
  company_name: string | null
  first_name: string | null
  last_name: string | null
  display_name: string
  address: string | null
  address2: string | null
  city: string | null
  state: string | null
  zip: string | null
  office_phone: string | null
  home_phone: string | null
  cell_phone: string | null
  email: string | null
}

interface CustomerUserData {
  display_name: string
  phone: string | null
  home_phone: string | null
  cell_phone: string | null
  email: string | null
}

interface CaseContactData {
  id: number
  category: string | null
  is_phone: boolean
  info: string
  notes: string | null
}

interface SubjectAddress {
  id: number
  street: string | null
  street2: string | null
  subdivision: string | null
  city: string | null
  state: string | null
  zip: string | null
  full_address: string
  notes: string | null
}

interface SubjectAlias {
  id: number
  full_name: string
}

interface SubjectContact {
  id: number
  category: string | null
  is_phone: boolean
  info: string | null
  notes: string | null
}

interface SubjectVehicle {
  id: number
  description: string | null
  tag: string | null
  tag_state: string | null
  display_name: string
  notes: string | null
}

interface EmploymentRecord {
  id: number
  company_name: string | null
  position: string | null
  address: string | null
  city: string | null
  state: string | null
  zip: string | null
  phone: string | null
  start_date: string | null
  end_date: string | null
  schedule: string | null
  supervisor: string | null
  display_name: string
  full_address: string
  date_range: string | null
}

interface SubjectPhoto {
  id: number
  filename: string | null
  caption: string | null
  display_name: string
  focal_point_x?: number | null
  focal_point_y?: number | null
  created_at: string
  image_url: string | null
  thumb_url: string | null
}

interface SubjectData {
  id: number
  subject_type: string | null
  type_description: string | null
  full_name: string
  display_name: string
  prefix: string | null
  first_name: string | null
  middle_name: string | null
  last_name: string | null
  suffix: string | null
  address: string | null
  address2: string | null
  subdivision: string | null
  city: string | null
  state: string | null
  zip: string | null
  home_phone: string | null
  cell_phone: string | null
  work_phone: string | null
  email: string | null
  sex: string | null
  race: string | null
  marital_status: string | null
  spouse_name: string | null
  divorce_date: string | null
  dependents: string | null
  description: string | null
  hobbies: string | null
  date_of_birth: string | null
  drivers_license: string | null
  drivers_license_state: string | null
  place_of_employment: string | null
  poe_address: string | null
  poe_city: string | null
  poe_state: string | null
  poe_zip: string | null
  work_schedule: string | null
  attorney: string | null
  law_firm: string | null
  ins_date_of_loss: string | null
  ins_benefits: string | null
  ins_working: string | null
  ins_rehab: string | null
  ins_rehab_loc: string | null
  ins_contact: string | null
  ins_contact_phone: string | null
  ins_may_we_contact: string | null
  ins_accident: string | null
  ins_injury: string | null
  ins_limits: string | null
  ins_appts: string | null
  ins_appt_info: string | null
  ins_prev_surveillance: string | null
  ins_prev_surveillance_date: string | null
  addresses: SubjectAddress[]
  aliases: SubjectAlias[]
  contacts: SubjectContact[]
  vehicles: SubjectVehicle[]
  employment_records: EmploymentRecord[]
  photos: SubjectPhoto[]
}

interface Props {
  case_record: CaseRecord
  customer: CustomerData
  customer_user: CustomerUserData | null
  case_contacts: CaseContactData[]
  subjects: SubjectData[]
}

/* ── Helpers ────────────────────────────────────────────── */

function formatDateOnly(date: string | null): string {
  return formatUSDate(date)
}

function googleMapsUrl(
  address: string | null,
  city: string | null,
  state: string | null,
  zip: string | null,
): string {
  const parts = [address, city, [state, zip].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ")
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts)}`
}

function fullAddress(
  street: string | null,
  street2: string | null,
  city: string | null,
  state: string | null,
  zip: string | null,
): string {
  const lines: string[] = []
  if (street) lines.push(street)
  if (street2) lines.push(street2)
  const csz = [city, [state, zip].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ")
  if (csz) lines.push(csz)
  return lines.join("\n")
}

function hasInsuranceFields(s: SubjectData): boolean {
  return !!(
    s.ins_date_of_loss ||
    s.ins_benefits ||
    s.ins_working ||
    s.ins_rehab ||
    s.ins_contact ||
    s.ins_injury ||
    s.ins_accident ||
    s.ins_limits ||
    s.ins_appts ||
    s.ins_prev_surveillance
  )
}

/* ── Label / Value Row ──────────────────────────────────── */

function LabelValue({
  label,
  value,
  className,
}: {
  label: string
  value: React.ReactNode
  className?: string
}) {
  if (!value) return null
  return (
    <div className={`flex gap-2 ${className ?? ""}`}>
      <span className="text-hpi-dark-gray shrink-0 font-semibold">{label}</span>
      <span className="text-hpi-dark-gray">{value}</span>
    </div>
  )
}

/* ── Subject Block ──────────────────────────────────────── */

function SubjectBlock({ subject }: { subject: SubjectData }) {
  const s = subject
  const currentAddr = fullAddress(s.address, s.address2, s.city, s.state, s.zip)
  const raceGender = [s.race, s.sex].filter(Boolean).join(", ")
  const marital = s.marital_status ?? ""

  const poeAddr = fullAddress(
    s.poe_address,
    null,
    s.poe_city,
    s.poe_state,
    s.poe_zip,
  )

  return (
    <div className="mb-8">
      {/* Subject header bar */}
      <div className="flex">
        {/* PEOPLE column header */}
        <div className="mr-4 flex-1">
          <div className="bg-hpi-dark-blue px-4 py-2 text-sm font-bold text-white uppercase">
            People
          </div>
          <div className="border-hpi-light-gray flex items-center gap-2 border-b px-4 py-2">
            <span className="text-hpi-dark-gray text-xs font-semibold uppercase">
              {s.subject_type ?? "Subject"}:
            </span>
            <span className="text-hpi-dark-gray text-sm">{s.display_name}</span>
          </div>
        </div>
        {/* PHOTOS column header */}
        <div className="w-[400px] shrink-0">
          <div className="bg-hpi-dark-blue px-4 py-2 text-sm font-bold text-white uppercase">
            Photos
          </div>
          <div className="border-hpi-light-gray border-b px-4 py-2">
            <span className="text-hpi-dark-gray text-xs font-semibold uppercase">
              {s.subject_type ?? "Subject"}:
            </span>{" "}
            <span className="text-hpi-dark-gray text-sm">{s.display_name}</span>
          </div>
        </div>
      </div>

      {/* Subject detail content */}
      <div className="flex">
        {/* Left: detail fields */}
        <div className="mr-4 flex-1 space-y-4 px-4 py-4 text-sm">
          {/* Identity section */}
          <div className="grid grid-cols-3 gap-x-6 gap-y-1">
            <LabelValue label="Subject" value={s.full_name} />
            <LabelValue
              label="DOB"
              value={s.date_of_birth ? formatDateOnly(s.date_of_birth) : null}
            />
            <LabelValue label="Race/Gender" value={raceGender ?? null} />
            <LabelValue label="Description" value={s.description} />
            <LabelValue label="Marital" value={marital ?? null} />
            {s.spouse_name && (
              <LabelValue label="Spouse" value={s.spouse_name} />
            )}
            {s.divorce_date && (
              <LabelValue label="Divorce Date" value={s.divorce_date} />
            )}
            {s.dependents && (
              <LabelValue label="Dependents" value={s.dependents} />
            )}
          </div>

          {/* Home address */}
          {currentAddr && (
            <div className="border-hpi-light-gray border-t border-dotted pt-3">
              <LabelValue
                label="Home Address"
                value={
                  <a
                    href={googleMapsUrl(s.address, s.city, s.state, s.zip)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-hpi-active-blue whitespace-pre-line"
                  >
                    {currentAddr}
                  </a>
                }
              />
            </div>
          )}

          {/* Additional addresses */}
          {s.addresses.length > 0 && (
            <div className="border-hpi-light-gray border-t border-dotted pt-3">
              <span className="text-hpi-dark-gray font-semibold">
                Additional Addresses
              </span>
              {s.addresses.map((addr) => (
                <div key={addr.id} className="mt-1 ml-4">
                  <a
                    href={googleMapsUrl(
                      addr.street,
                      addr.city,
                      addr.state,
                      addr.zip,
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-hpi-active-blue whitespace-pre-line"
                  >
                    {addr.full_address}
                  </a>
                  {addr.notes && (
                    <div className="text-hpi-med-blue mt-0.5 text-xs">
                      {addr.notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Aliases */}
          {s.aliases.length > 0 && (
            <div className="border-hpi-light-gray border-t border-dotted pt-3">
              <LabelValue
                label="Aliases"
                value={s.aliases.map((a) => a.full_name).join("; ")}
              />
            </div>
          )}

          {/* Phones & contacts */}
          {[s.home_phone, s.cell_phone, s.work_phone, s.email].some(
            Boolean,
          ) && (
            <div className="border-hpi-light-gray grid grid-cols-3 gap-x-6 gap-y-1 border-t border-dotted pt-3">
              {s.home_phone && (
                <LabelValue
                  label="Home Phone"
                  value={formatPhone(s.home_phone)}
                />
              )}
              {s.cell_phone && (
                <LabelValue
                  label="Cell Phone"
                  value={formatPhone(s.cell_phone)}
                />
              )}
              {s.work_phone && (
                <LabelValue
                  label="Work Phone"
                  value={formatPhone(s.work_phone)}
                />
              )}
              {s.email && <LabelValue label="Email" value={s.email} />}
            </div>
          )}

          {/* Additional contacts */}
          {s.contacts.length > 0 && (
            <div className="border-hpi-light-gray border-t border-dotted pt-3">
              <span className="text-hpi-dark-gray font-semibold">
                Additional Contacts
              </span>
              {s.contacts.map((c) => (
                <div key={c.id} className="mt-1 ml-4">
                  <span className="text-hpi-med-blue text-xs font-medium">
                    {c.category}
                  </span>{" "}
                  <span>
                    {c.is_phone ? (
                      formatPhone(c.info)
                    ) : ensureUrl(c.info) ? (
                      <a
                        href={ensureUrl(c.info)!}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-hpi-active-blue hover:underline"
                      >
                        {c.info}
                      </a>
                    ) : c.info && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.info) ? (
                      <a
                        href={`mailto:${c.info}`}
                        className="text-hpi-active-blue hover:underline"
                      >
                        {c.info}
                      </a>
                    ) : (
                      c.info
                    )}
                  </span>
                  {c.notes && (
                    <span className="text-hpi-med-blue ml-2 text-xs">
                      ({c.notes})
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Attorney */}
          {[s.attorney, s.law_firm].some(Boolean) && (
            <div className="border-hpi-light-gray grid grid-cols-2 gap-x-6 gap-y-1 border-t border-dotted pt-3">
              {s.law_firm && <LabelValue label="Law Firm" value={s.law_firm} />}
              {s.attorney && <LabelValue label="Attorney" value={s.attorney} />}
            </div>
          )}

          {/* Vehicles */}
          {s.vehicles.length > 0 && (
            <div className="border-hpi-light-gray border-t border-dotted pt-3">
              <span className="text-hpi-dark-gray font-semibold">Vehicles</span>
              {s.vehicles.map((v) => (
                <div key={v.id} className="mt-1 ml-4">
                  <div>{v.display_name}</div>
                  {(v.tag || v.tag_state) && (
                    <div className="text-hpi-med-blue text-xs">
                      Tag: {[v.tag, v.tag_state].filter(Boolean).join(" - ")}
                    </div>
                  )}
                  {v.notes && (
                    <div className="text-hpi-dark-gray mt-0.5 text-xs whitespace-pre-wrap">
                      {v.notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Employment info */}
          {(s.place_of_employment || s.employment_records.length > 0) && (
            <div className="border-hpi-light-gray border-t border-dotted pt-3">
              <span className="text-hpi-dark-gray font-semibold">
                Employment Info
              </span>
              {s.place_of_employment && (
                <div className="mt-1 ml-4">
                  <div>{s.place_of_employment}</div>
                  {poeAddr && (
                    <div className="whitespace-pre-line">{poeAddr}</div>
                  )}
                </div>
              )}
              {s.employment_records.map((e) => (
                <div key={e.id} className="mt-2 ml-4">
                  <div className="font-medium">{e.display_name}</div>
                  {e.full_address && (
                    <div className="whitespace-pre-line">{e.full_address}</div>
                  )}
                  {e.date_range && (
                    <div className="text-hpi-med-blue text-xs">
                      {e.date_range}
                    </div>
                  )}
                  {e.schedule && (
                    <div className="text-xs">Schedule: {e.schedule}</div>
                  )}
                  {e.supervisor && (
                    <div className="text-xs">Supervisor: {e.supervisor}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Work schedule */}
          {s.work_schedule && (
            <div className="border-hpi-light-gray border-t border-dotted pt-3">
              <LabelValue label="Work Schedule" value={s.work_schedule} />
            </div>
          )}

          {/* Hobbies/Details */}
          {s.hobbies && (
            <div className="border-hpi-light-gray border-t border-dotted pt-3">
              <LabelValue
                label="Details"
                value={<span className="whitespace-pre-wrap">{s.hobbies}</span>}
              />
            </div>
          )}

          {/* Driver's license */}
          {s.drivers_license && (
            <div className="border-hpi-light-gray border-t border-dotted pt-3">
              <LabelValue
                label="DL#"
                value={`${s.drivers_license}${s.drivers_license_state ? ` (${s.drivers_license_state})` : ""}`}
              />
            </div>
          )}

          {/* Insurance claim section */}
          {hasInsuranceFields(s) && (
            <div className="border-hpi-dark-blue mt-4 border-t-2 pt-3">
              <div className="text-hpi-dark-blue mb-2 font-bold uppercase">
                Insurance Claim Details
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                {s.ins_date_of_loss && (
                  <LabelValue label="Date of Loss" value={s.ins_date_of_loss} />
                )}
                {s.ins_injury && (
                  <LabelValue label="Injury" value={s.ins_injury} />
                )}
                {s.ins_benefits && (
                  <LabelValue label="Benefits" value={s.ins_benefits} />
                )}
                {s.ins_working && (
                  <LabelValue label="Working" value={s.ins_working} />
                )}
                {s.ins_rehab && (
                  <LabelValue label="Rehab" value={s.ins_rehab} />
                )}
                {s.ins_rehab_loc && (
                  <LabelValue label="Rehab Location" value={s.ins_rehab_loc} />
                )}
                {s.ins_contact && (
                  <LabelValue label="Contact" value={s.ins_contact} />
                )}
                {s.ins_contact_phone && (
                  <LabelValue
                    label="Contact Phone"
                    value={formatPhone(s.ins_contact_phone)}
                  />
                )}
                {s.ins_may_we_contact && (
                  <LabelValue
                    label="May We Contact"
                    value={s.ins_may_we_contact}
                  />
                )}
                {s.ins_accident && (
                  <LabelValue label="Accident" value={s.ins_accident} />
                )}
                {s.ins_limits && (
                  <LabelValue label="Limits" value={s.ins_limits} />
                )}
                {s.ins_appts && (
                  <LabelValue label="Appointments" value={s.ins_appts} />
                )}
                {s.ins_appt_info && (
                  <LabelValue label="Appt Info" value={s.ins_appt_info} />
                )}
                {s.ins_prev_surveillance && (
                  <LabelValue
                    label="Prev Surveillance"
                    value={s.ins_prev_surveillance}
                  />
                )}
                {s.ins_prev_surveillance_date && (
                  <LabelValue
                    label="Prev Surv Date"
                    value={s.ins_prev_surveillance_date}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right: photos */}
        <div className="w-[400px] shrink-0 px-4 py-4">
          {s.photos.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {s.photos.map((photo) => (
                <div key={photo.id} className="text-center">
                  {photo.thumb_url ? (
                    <a
                      href={photo.image_url ?? photo.thumb_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={photo.thumb_url}
                        alt={photo.display_name}
                        className="h-[140px] w-full rounded object-cover"
                        style={{ objectPosition: objectPositionStyle(photo) }}
                      />
                    </a>
                  ) : (
                    <div className="bg-hpi-light-gray border-hpi-med-gray text-hpi-med-gray flex h-[140px] items-center justify-center rounded border text-xs">
                      No image
                    </div>
                  )}
                  <div className="text-hpi-dark-gray mt-1 text-xs">
                    {formatDate(photo.created_at)}
                  </div>
                  {photo.caption && (
                    <div className="text-hpi-med-blue text-xs">
                      {photo.caption}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-hpi-med-gray text-xs italic">No photos</div>
          )}
        </div>
      </div>
    </div>
  )
}

/* ── Main Component ─────────────────────────────────────── */

export default function Worksheet({
  case_record,
  customer,
  customer_user,
  case_contacts,
  subjects,
}: Props) {
  const c = case_record
  const cust = customer

  const customerAddress = fullAddress(
    cust.address,
    cust.address2,
    cust.city,
    cust.state,
    cust.zip,
  )

  return (
    <>
      <Head title={`Worksheet - ${c.display_name}`} />

      <div className="mx-auto max-w-[1400px] bg-white p-8 text-sm print:p-4">
        {/* ── CASE WORKSHEET heading ── */}
        <div className="mb-6">
          <h2 className="text-hpi-active-blue text-2xl font-bold uppercase">
            Case Worksheet
          </h2>
          <div className="text-hpi-red text-xs font-semibold tracking-wider uppercase">
            Confidential
          </div>
        </div>

        {/* ── Case Header ── */}
        <div className="mb-6 flex gap-8">
          {/* Left column */}
          <div className="flex-1">
            <h1 className="text-hpi-dark-gray mb-3 text-xl font-bold uppercase">
              {c.display_name}
            </h1>
            <div className="space-y-1">
              <LabelValue label="CUSTOMER:" value={cust.display_name} />
              {cust.office_phone && (
                <LabelValue
                  label="OFFICE:"
                  value={formatPhone(cust.office_phone)}
                />
              )}
              {cust.home_phone && (
                <LabelValue
                  label="HOME:"
                  value={formatPhone(cust.home_phone)}
                />
              )}
              {cust.cell_phone && (
                <LabelValue
                  label="CELL:"
                  value={formatPhone(cust.cell_phone)}
                />
              )}
              {cust.email && <LabelValue label="EMAIL:" value={cust.email} />}
              {customerAddress && (
                <LabelValue
                  label="ADDRESS:"
                  value={
                    <a
                      href={googleMapsUrl(
                        cust.address,
                        cust.city,
                        cust.state,
                        cust.zip,
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-hpi-active-blue whitespace-pre-line"
                    >
                      {customerAddress}
                    </a>
                  }
                />
              )}
            </div>

            {/* Attorney section */}
            {c.has_attorney && (
              <div className="mt-3 space-y-1">
                <LabelValue
                  label="CUSTOMER ATTORNEY:"
                  value={c.attorney_name}
                />
                {c.attorney_phone && (
                  <LabelValue
                    label="ATTORNEY PHONE:"
                    value={formatPhone(c.attorney_phone)}
                  />
                )}
                {c.attorney_law_firm && (
                  <LabelValue label="LAW FIRM:" value={c.attorney_law_firm} />
                )}
                {c.attorney_email && (
                  <LabelValue
                    label="ATTORNEY EMAIL:"
                    value={c.attorney_email}
                  />
                )}
              </div>
            )}

            {/* Customer contact */}
            {customer_user && (
              <div className="mt-3 space-y-1">
                <LabelValue
                  label="CONTACT:"
                  value={customer_user.display_name}
                />
                {customer_user.phone && (
                  <LabelValue
                    label="OFFICE:"
                    value={formatPhone(customer_user.phone)}
                  />
                )}
                {customer_user.cell_phone && (
                  <LabelValue
                    label="CELL:"
                    value={formatPhone(customer_user.cell_phone)}
                  />
                )}
                {customer_user.home_phone && (
                  <LabelValue
                    label="HOME:"
                    value={formatPhone(customer_user.home_phone)}
                  />
                )}
                {customer_user.email && (
                  <LabelValue label="EMAIL:" value={customer_user.email} />
                )}
              </div>
            )}
          </div>

          {/* Right column */}
          <div className="w-[340px] shrink-0">
            <div className="mb-3">
              <span
                className={`text-lg font-medium ${c.open ? "text-hpi-active-blue" : "text-hpi-med-gray"}`}
              >
                {c.open ? "Active" : "Closed"} - {c.investigation_type ?? ""}
              </span>
            </div>
            <div className="space-y-1">
              <LabelValue label="INTAKE BY:" value="-" />
              <LabelValue label="MANAGER:" value={c.manager ?? "-"} />
              <LabelValue label="OPENED:" value={formatDate(c.open_date)} />
              <LabelValue label="CASE #:" value={c.case_number} />
            </div>

            {c.objectives && (
              <div className="mt-3">
                <LabelValue label="OBJECTIVE:" value={c.objectives} />
              </div>
            )}

            {c.results_notes && (
              <div className="mt-2">
                <span className="text-hpi-dark-gray font-semibold">
                  FINAL ASSESSMENT:{" "}
                </span>
                <span className="text-hpi-dark-green font-semibold">
                  {c.accepted_text ?? ""}
                </span>
                {" - "}
                <span className="text-hpi-dark-gray">{c.results_notes}</span>
              </div>
            )}
          </div>
        </div>

        {/* ── Additional Case Contacts ── */}
        {case_contacts.length > 0 && (
          <div className="border-hpi-light-gray mb-6 border-t border-dotted pt-3">
            <span className="text-hpi-dark-gray font-semibold">
              Case Contacts
            </span>
            <div className="mt-1 grid grid-cols-3 gap-2">
              {case_contacts.map((cc) => (
                <div key={cc.id} className="text-xs">
                  <span className="text-hpi-med-blue font-medium">
                    {cc.category}:
                  </span>{" "}
                  {cc.is_phone ? formatPhone(cc.info) : cc.info}
                  {cc.notes && (
                    <span className="text-hpi-med-gray ml-1">({cc.notes})</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Case Notes ── */}
        {c.notes && (
          <div className="border-hpi-light-gray mr-[416px] mb-6 border-t border-dotted pt-3">
            <LabelValue
              label="Case Notes:"
              value={<span className="whitespace-pre-wrap">{c.notes}</span>}
            />
          </div>
        )}

        {/* ── Subject Blocks ── */}
        {subjects.map((subject) => (
          <SubjectBlock key={subject.id} subject={subject} />
        ))}

        {subjects.length === 0 && (
          <div className="text-hpi-med-gray py-12 text-center">
            No subjects on this case.
          </div>
        )}
      </div>
    </>
  )
}
